<script setup lang="ts">
import VaultFileBrowser from '@/components/file_vault/VaultFileBrowser.vue';
import GaiaFilesList from '@/components/file_vault/GaiaFilesList.vue';
import GaiaVaultUploadPanel from '@/components/design/domain/fileVault/GaiaVaultUploadPanel.vue';
import type { VaultUploadPanelApi } from '@/components/design/domain/fileVault/vaultUploadPanel';
import { useFileSelection } from '@/composables/useFileSelection';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useIntegrationsStore } from '@/stores/integrationsStore';
import { useVaultStore } from '@/stores/vaultStore';

import { computed, ref, watch } from 'vue';

// Props
interface Props {
  title?: string;
  collection?: string;
  promptCollections?: { uid: string; name: string }[];
  selectedKeys: string[];
  preUploadedFiles?: File[];
  showFileVaultTab?: boolean;
  showUploadTab?: boolean;
  showGaiaTab?: boolean;
  fileVaultFileOnlySelection?: boolean;
  initialTab?: string;
}

// Emits
interface Emits {
  (e: 'update:selectedKeys', keys: string[]): void;
  (e: 'tab-change', tab: string): void;
  (e: 'files-uploaded', fileUids: string[]): void;
  (e: 'upload-selection-change', count: number): void;
}

const props = withDefaults(defineProps<Props>(), {
  title: undefined,
  collection: undefined,
  promptCollections: undefined,
  preUploadedFiles: undefined,
  showFileVaultTab: true,
  showUploadTab: true,
  showGaiaTab: true,
  fileVaultFileOnlySelection: false,
  initialTab: undefined,
});
const emit = defineEmits<Emits>();

// Store initialization
const vaultStore = useVaultStore();
const gaiaStore = useGaiaStore();
const integrationsStore = useIntegrationsStore();

// Use the file selection composable
const {
  selectedFileKeys,
  parsedKeys,
  fileVaultKeys,
  gaiaKeys,
  updateVaultSelection,
  updateGaiaSelection,
} = useFileSelection();

// Local state
const modalActiveTab = ref<string>(
  props.initialTab ?? (props.showUploadTab ? 'upload' : 'file-vault')
);

// Sync with parent component via v-model
watch(
  () => props.selectedKeys,
  newKeys => {
    selectedFileKeys.value = newKeys;
  },
  { immediate: true }
);

watch(selectedFileKeys, newKeys => {
  emit('update:selectedKeys', newKeys);
});

// Watch for tab changes and emit to parent
watch(
  modalActiveTab,
  newTab => {
    emit('tab-change', newTab);
  },
  { immediate: true }
);

watch(
  () => [props.showUploadTab, props.showFileVaultTab, props.showGaiaTab] as const,
  () => {
    const availableTabs = [
      props.showUploadTab ? 'upload' : null,
      props.showFileVaultTab ? 'file-vault' : null,
      props.showFileVaultTab && props.showGaiaTab && integrationsStore.activeIntegrations.gaia
        ? 'gaia-sections'
        : null,
    ].filter((tab): tab is string => tab !== null);

    if (!availableTabs.includes(modalActiveTab.value)) {
      modalActiveTab.value = availableTabs[0] ?? 'upload';
    }
  },
  { immediate: true }
);

// Split selectedKeys into vault and GAIA selections
const fileVaultSelection = computed({
  get: () => fileVaultKeys.value,
  set: (value: string[]) => {
    updateVaultSelection(value);
  },
});

const gaiaSelection = computed({
  get: () => gaiaKeys.value,
  set: (value: string[]) => {
    updateGaiaSelection(value);
  },
});

/** Stable for the panel, which replaces its list whenever this changes. */
const preUploadedFiles = computed<File[]>(() => props.preUploadedFiles ?? []);

// Computed property for total selected count
const selectedFilesCount = computed(() => selectedFileKeys.value.length);

// Event handlers
function handleGaiaSelectionChange(keys: string[]): void {
  gaiaSelection.value = keys;
}

function handleFilesUploaded(fileUids: string[]): void {
  // Convert uploaded file UIDs to file keys
  const fileKeys = fileUids.map(uid => `file-${uid}`);

  // Add newly uploaded files to existing selection (preserve previous selections)
  const currentSelection = [...selectedFileKeys.value];
  const newSelection = [...new Set([...currentSelection, ...fileKeys])]; // Remove duplicates
  selectedFileKeys.value = newSelection;

  // Refresh vault directory
  void vaultStore.getVaultDirectory();

  // Emit to parent component
  emit('files-uploaded', fileUids);
}

function handleUploadSelectionChange(count: number): void {
  emit('upload-selection-change', count);
}

// Component refs
const uploadPanelRef = ref<VaultUploadPanelApi | null>(null);

// Expose methods for parent components
defineExpose({
  getParsedKeys: () => parsedKeys.value,
  getAllSelectedUids: () => {
    const { fileKeys, dirKeys, gaiaKeys } = parsedKeys.value;
    return {
      fileUids: fileKeys.map((key: string) => key.replace('file-', '')),
      dirUids: dirKeys.map((key: string) => key.replace('dir-', '')),
      gaiaUids: gaiaKeys.map((key: string) => key.replace('gaia-', '')),
    };
  },
  /**
   * Upload the Upload tab's files into the folder picked there and return
   * their vault uids. Throws when one failed — the tab lists why and keeps it.
   */
  uploadSelectedFiles: async (): Promise<string[]> => {
    const panel = uploadPanelRef.value;
    if (!panel || panel.files().length === 0) return [];
    const uploaded = await panel.upload();
    if (!uploaded) throw new Error('Some files could not be uploaded.');
    const fileUids = uploaded.map(({ fileUid }) => fileUid);
    panel.reset();
    handleFilesUploaded(fileUids);
    return fileUids;
  },
  getSelectedFiles: (): File[] => uploadPanelRef.value?.files() ?? [],
});
</script>

<template>
  <div class="modal-content">
    <NForm>
      <NTabs v-model:value="modalActiveTab" type="line" size="medium" class="modal-tabs">
        <NTabPane v-if="props.showUploadTab" name="upload" tab="Upload">
          <div class="upload-area-container">
            <!-- Lands in a folder the user picks, My Vault unless changed. -->
            <gaia-vault-upload-panel
              ref="uploadPanelRef"
              selectable-destination
              :initial-files="preUploadedFiles"
              :unpack-archives="false"
              @update:file-count="handleUploadSelectionChange"
            />
          </div>
        </NTabPane>

        <NTabPane
          v-if="props.showFileVaultTab"
          name="file-vault"
          :tab="`Files${fileVaultSelection.length > 0 ? ` (${fileVaultSelection.length})` : ''}`"
          class="file-vault-tab"
        >
          <!-- Contracts open like folders; their files are picked one by one. -->
          <VaultFileBrowser
            v-model:selected-keys="fileVaultSelection"
            :file-only-selection="props.fileVaultFileOnlySelection"
            contracts="open"
          />
        </NTabPane>

        <NTabPane
          v-if="
            props.showFileVaultTab && props.showGaiaTab && integrationsStore.activeIntegrations.gaia
          "
          name="gaia-sections"
          :tab="`GAIA Sections${gaiaSelection.length > 0 ? ` (${gaiaSelection.length})` : ''}`"
          class="gaia-sections-tab"
        >
          <GaiaFilesList
            :gaia-store="gaiaStore"
            :selected-file-keys="gaiaSelection"
            :total-selected-count="selectedFilesCount"
            @selection-change="handleGaiaSelectionChange"
          />
        </NTabPane>
      </NTabs>
    </NForm>
  </div>
</template>

<style scoped>
.modal-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.modal-content :deep(.n-form) {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.modal-content :deep(.n-tabs) {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.modal-content :deep(.n-tabs-content) {
  flex: 1 1 0;
  overflow: hidden;
  min-height: 0;
}

.modal-content :deep(.n-tab-pane) {
  height: 100%;
  overflow: hidden;
}

.upload-area-container {
  max-height: 60vh;
  overflow-y: auto;
  padding-bottom: 200px; /* White space at bottom for scrolling */
}

/* File vault tab styling for proper scrolling */
.file-vault-tab {
  height: 100%;
  overflow: hidden;
}

/* GAIA sections tab styling for proper scrolling */
.gaia-sections-tab {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.gaia-sections-tab :deep(.n-tab-pane__content) {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
